import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { SponsorsSection } from "../components/Sponsors";
import CertificatesSection from "../components/CertificatesSection";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Lightbulb, Rocket, Target, Trophy, Code2, Users, Calendar, Zap, ArrowRight, CheckCircle2, Mail, Sparkles, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { track, newEventId, trackViewContent } from "../lib/analytics";
import { FEE_DEFAULT, TEAM_MIN_DEFAULT, TEAM_MAX_DEFAULT } from "../lib/appDefaults";

const formatINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
// Round displayed prizes to nearest ₹500 (display-only — stored amounts unchanged)
const roundDisplay = (n) => Math.round((Number(n) || 0) / 500) * 500;
const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; }
};

export default function Landing() {
  const { config, user } = useAuth();
  const fee = config?.entry_fee ?? FEE_DEFAULT;
  const minSize = config?.min_team_size ?? TEAM_MIN_DEFAULT;
  const maxSize = config?.max_team_size ?? TEAM_MAX_DEFAULT;

  const [prizes, setPrizes] = useState({ prize_1: 15000, prize_2: 10000, prize_3: 5000, total_registrations: 0, frozen: false });
  useEffect(() => {
    // Fetch once on page load — backend recalculates the cache daily at midnight IST
    let alive = true;
    (async () => {
      try { const { data } = await api.get("/prizes"); if (alive) setPrizes(data); } catch (e) { console.error("Failed to load prize pool:", e); }
    })();
    return () => { alive = false; };
  }, []);
  const livePool = roundDisplay(prizes.prize_1) + roundDisplay(prizes.prize_2) + roundDisplay(prizes.prize_3);

  const [pubStats, setPubStats] = useState({ show_participants: false, show_teams: false });
  useEffect(() => {
    trackViewContent("landing", newEventId(), { content_name: "IdeaThon 2026" });
    let alive = true;
    (async () => {
      try { const { data } = await api.get("/public/stats"); if (alive) setPubStats(data); } catch (e) { /* stats are optional */ }
    })();
    return () => { alive = false; };
  }, []);
  // Scroll to the #section when arriving via a nav/footer anchor — handles fresh
  // page loads and cross-page links where the target renders after the hash jump.
  // scroll-padding-top (index.css) keeps it clear of the sticky navbar.
  useEffect(() => {
    const scrollToHash = () => {
      const id = window.location.hash.slice(1);
      if (!id) return;
      const el = document.getElementById(id);
      if (el) el.scrollIntoView();
    };
    const t1 = setTimeout(scrollToHash, 120);
    const t2 = setTimeout(scrollToHash, 650);
    window.addEventListener("hashchange", scrollToHash);
    return () => { clearTimeout(t1); clearTimeout(t2); window.removeEventListener("hashchange", scrollToHash); };
  }, []);

  const isClosed = config?.status === "completed";
  const isPreLaunch = config?.status === "pre_launch";
  const theme = config?.theme;

  // Pick the right "next step" CTA target based on the logged-in user's role.
  const ctaTarget = !user
    ? "/register"
    : user.role === "judge"
      ? "/judge/dashboard"
      : user.role === "admin"
        ? "/admin"
        : user.team_id ? "/team/dashboard" : "/team";

  const timeline = useMemo(() => {
    const exts = (config?.deadline_extensions || []);
    const wasExtended = (field) => exts.some(e => e.field === field);
    const lastExt = (field) => exts.filter(e => e.field === field).slice(-1)[0];
    return [
      { label: "Registration Opens", date: "Now Open", active: true, field: null },
      { label: "Team Lock Deadline", date: formatDate(config?.team_lock_deadline), field: "team_lock_deadline", extended: wasExtended("team_lock_deadline"), ext: lastExt("team_lock_deadline") },
      { label: "Round 1 Opens",  date: formatDate(config?.r1_open_at),  field: "r1_open_at",  extended: wasExtended("r1_open_at"),  ext: lastExt("r1_open_at") },
      { label: "Round 1 Closes", date: formatDate(config?.r1_close_at), field: "r1_close_at", extended: wasExtended("r1_close_at"), ext: lastExt("r1_close_at") },
      { label: "Round 2 Opens",  date: formatDate(config?.r2_open_at),  field: "r2_open_at",  extended: wasExtended("r2_open_at"),  ext: lastExt("r2_open_at") },
      { label: "Round 2 Closes", date: formatDate(config?.r2_close_at), field: "r2_close_at", extended: wasExtended("r2_close_at"), ext: lastExt("r2_close_at") },
      { label: "Finale & Awards", date: formatDate(config?.results_announce_at || config?.r2_close_at), field: "results_announce_at", extended: wasExtended("results_announce_at"), ext: lastExt("results_announce_at") },
    ];
  }, [config]);

  if (isPreLaunch) return <ComingSoon config={config} />;

  return (
    <div className="App" data-testid="landing-page">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden grid-bg">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-14 sm:pt-16 pb-6 md:pb-8 lg:pb-6">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8 fade-up">
              <div className="label-eyebrow mb-6 flex flex-wrap items-center gap-6" data-testid="hero-eyebrow">
                <span>NoobSync Innovate</span>
                <span>IdeaThon {config?.edition || ""}</span>
                {theme && <span className="text-brand">{theme}</span>}
                {isClosed && <span className="badge badge-disqualified">closed</span>}
              </div>
              <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.95] text-white" data-testid="hero-title">
                {isClosed ? <>This edition <span className="text-brand">has ended.</span></> : <>Build. <span className="text-brand">Compete.</span> <br />Win.</>}
              </h1>
              {!isClosed && (
                <p className="mt-5 text-base sm:text-lg font-medium text-gray-400" data-testid="hero-motto">AI executes. Humans innovate.</p>
              )}
              <p className="mt-4 text-lg sm:text-xl text-gray-400 max-w-2xl leading-relaxed">
                {isClosed
                  ? "Thanks to every student who shipped. Winners are below — see the full series archive for past editions and stay tuned for the next one."
                  : (config?.tagline || "An online, two-round build competition for student teams. Pitch your idea in Round 1, build a working prototype in Round 2, and compete for a ₹30,000 cash prize pool.")}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                {isClosed ? (
                  <>
                    <Link to="/results" className="btn-primary flex items-center gap-2" data-testid="closed-results-cta">
                      View Winners <ArrowRight className="w-4 h-4" strokeWidth={2} />
                    </Link>
                    <Link to="/editions" className="btn-secondary" data-testid="closed-editions-cta">Series Archive</Link>
                  </>
                ) : (
                  <>
                    <Link to={ctaTarget} className="btn-primary flex items-center gap-2" data-testid="hero-cta">
                      {user ? "Continue →" : "Register Now"} <ArrowRight className="w-4 h-4" strokeWidth={2} />
                    </Link>
                    <a href="#how" className="btn-secondary" data-testid="hero-secondary">Learn More</a>
                  </>
                )}
              </div>
              {!isClosed && (
                <p className="mt-4 text-sm text-gray-400" data-testid="hero-payment-trust">Secure payments powered by Razorpay</p>
              )}
            </div>

            <div className="lg:col-span-4 fade-up">
              <div className="card-surface border-l-2 border-brand">
                <div className="label-eyebrow">Entry Fee</div>
                <div className="font-display text-5xl mt-1 text-white" data-testid="hero-fee">{formatINR(fee)}<span className="text-lg text-gray-500"> /person</span></div>
                <div className="mt-5 grid grid-cols-2 gap-4">
                  <div>
                    <div className="label-eyebrow">Team Size</div>
                    <div className="font-display text-2xl text-white" data-testid="hero-team-size">{minSize}–{maxSize}</div>
                  </div>
                  <div>
                    <div className="label-eyebrow">Rounds</div>
                    <div className="font-display text-2xl text-white">02</div>
                  </div>
                </div>
                <div className="mt-5 pt-5 border-t border-white/5">
                  <div className="label-eyebrow">Prize Pool</div>
                  <div className="font-display text-2xl text-brand" data-testid="hero-prize">{formatINR(livePool)}</div>
                </div>
                {(pubStats.show_participants || pubStats.show_teams) && (
                  <div className="mt-5 pt-5 border-t border-white/5 grid grid-cols-2 gap-4" data-testid="hero-public-stats">
                    {pubStats.show_participants && (
                      <div>
                        <div className="label-eyebrow">Participants</div>
                        <div className="font-display text-2xl text-white" data-testid="hero-participants">{pubStats.total_participants ?? 0}</div>
                      </div>
                    )}
                    {pubStats.show_teams && (
                      <div>
                        <div className="label-eyebrow">Teams</div>
                        <div className="font-display text-2xl text-white" data-testid="hero-teams">{pubStats.total_teams ?? 0}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="max-w-7xl mx-auto px-5 sm:px-8 py-6 md:py-8 lg:py-8" data-testid="about-section">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="label-eyebrow mb-3">About</div>
            <h2 className="font-display text-4xl sm:text-5xl text-white leading-none">Where student ideas come to life.</h2>
          </div>
          <div className="lg:col-span-8 text-gray-400 text-lg leading-relaxed">
            <p>IdeaThon is a two-round build sprint for students. Bring an idea — rough or ready — pitch it in Round 1, build a working prototype in Round 2, and compete for a ₹30,000 cash prize pool. Participants who submit a valid Round 1 entry receive a verifiable certificate, whether or not they advance to Round 2.</p>
            <div className="mt-8 grid sm:grid-cols-3 gap-6">
              {[
                { i: Lightbulb, t: "Bold Ideas", d: "Pitch anything from a campus tool to a planet-scale product." },
                { i: Code2,     t: "Real Builds", d: "Shortlisted teams build and submit a working prototype in Round 2." },
                { i: Trophy,    t: "Real Stakes", d: `Compete for ₹${livePool.toLocaleString("en-IN")} in cash prizes. Selected projects may also be featured across NoobSync channels.` },
              ].map(({ i: Icon, t, d }, idx) => (
                <div key={idx} className="border-t border-white/10 pt-5" data-testid={`about-pillar-${idx}`}>
                  <Icon className="w-5 h-5 text-brand mb-3" strokeWidth={1.5} />
                  <div className="font-display text-lg text-white">{t}</div>
                  <div className="text-sm text-gray-500 mt-1">{d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="bg-[#080C18] border-y border-white/5 py-6 md:py-8 lg:py-8" data-testid="how-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="label-eyebrow mb-3">How It Works</div>
          <h2 className="font-display text-4xl sm:text-5xl text-white leading-none">Two rounds. Pitch it, then build it.</h2>
          <div className="mt-8 grid md:grid-cols-12 gap-6">
            <div className="card-surface card-hover relative md:col-span-5" data-testid="round-1-card">
              <Target className="w-6 h-6 text-brand mb-4" strokeWidth={1.5} />
              <div className="font-display text-2xl text-white">Round 1: Pitch</div>
              <p className="text-gray-400 mt-2">Submit your idea with a short problem statement, proposed solution, tech stack, and a recorded pitch video (2–3 minutes; YouTube, Loom, or Google Drive links accepted).</p>
              <ul className="mt-5 space-y-2 text-sm text-gray-400">
                {["Problem & solution write-up", "Tech stack selection", "Recorded pitch video link (2–3 mins)"].map(x => (
                  <li key={x} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand mt-0.5 flex-shrink-0" strokeWidth={1.5} />{x}</li>
                ))}
              </ul>
            </div>
            <div className="card-surface card-hover relative md:col-span-7" data-testid="round-2-card">
              <Rocket className="w-6 h-6 text-brand mb-4" strokeWidth={1.5} />
              <div className="font-display text-2xl text-white">Round 2: Build</div>
              <p className="text-gray-400 mt-2">Shortlisted teams build a working prototype and submit a recorded pitch video, source repository, and optional live demo link.</p>
              <ul className="mt-5 space-y-2 text-sm text-gray-400">
                {["Recorded pitch video (up to 5 mins, any link)", "Source repository link (GitHub or any public repo)", "Live demo link (optional)", "Short write-up of what changed from Round 1"].map(x => (
                  <li key={x} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand mt-0.5 flex-shrink-0" strokeWidth={1.5} />{x}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section id="timeline" className="max-w-7xl mx-auto px-5 sm:px-8 py-6 md:py-8 lg:py-8" data-testid="timeline-section">
        <div className="grid md:grid-cols-[1fr_2fr] md:gap-16 items-start">
          <div>
            <div className="label-eyebrow mb-3">Timeline</div>
            <h2 className="font-display text-4xl md:text-3xl lg:text-4xl text-white leading-tight md:max-w-[8em]">Mark your calendar.</h2>
          </div>
          <div className="min-w-0">
            <div className="mt-10 md:mt-0 relative">
              <div className="absolute left-3 md:left-5 lg:left-[22px] top-2 bottom-2 w-px bg-white/10" />
              <div className="space-y-8 md:space-y-4">
                {timeline.map((t, i) => (
                  <div key={i} className="relative pl-12 md:pl-0 md:grid md:grid-cols-[40px_180px_1fr] lg:grid-cols-[44px_220px_1fr] md:items-center md:py-1" data-testid={`timeline-item-${i}`}>
                    <div className={`absolute left-0 top-1.5 md:static md:justify-self-center w-6 h-6 border-2 ${i === 0 ? "bg-brand border-brand" : "border-white/30 bg-[#0A0F1E]"} flex items-center justify-center`}>
                      {i === 0 ? <Zap className="w-3 h-3 text-black" strokeWidth={3} /> : null}
                    </div>
                    <div className="text-[0.7rem] font-mono uppercase tracking-[0.2em] text-gray-400 md:pr-4">{t.date}</div>
                    <div className="mt-1 md:mt-0">
                      <div className="font-display text-xl text-white flex items-center gap-2">
                        {t.label}
                        {t.extended && (
                          <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 bg-brand/15 text-brand border border-brand/40" title={t.ext ? `Originally ${formatDate(t.ext.previous)} · extended ${formatDate(t.ext.extended_at)}` : "Extended"} data-testid={`timeline-extended-${i}`}>
                            Extended
                          </span>
                        )}
                      </div>
                      {t.extended && t.ext && (
                        <div className="text-xs text-gray-500 mt-1 font-mono" data-testid={`timeline-was-${i}`}>was {formatDate(t.ext.previous)}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRIZE POOL */}
      <section className="bg-[#080C18] border-y border-white/5 py-8 md:py-10 lg:py-10" data-testid="prize-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="label-eyebrow mb-3">Prize Pool</div>
            <h2 className="font-display text-4xl sm:text-5xl text-white leading-none">
              ₹30,000 in cash prizes.
            </h2>
            <p className="text-gray-400 mt-4 max-w-md">Cash prizes will be awarded to the Champion, Runner-up, and Third-place teams. See the complete prize breakdown below.</p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { p: "01", t: "Champion", a: roundDisplay(prizes.prize_1), hl: true },
              { p: "02", t: "Runner-up", a: roundDisplay(prizes.prize_2) },
              { p: "03", t: "Third", a: roundDisplay(prizes.prize_3) },
            ].map((x, i) => (
              <div key={i} className={`card-surface relative ${x.hl ? "!border-brand/50 sm:scale-105 sm:-translate-y-2 sm:!py-8 shadow-2xl shadow-black/60 z-10" : ""}`} data-testid={`prize-tier-${i}`}>
                <div className="font-mono text-brand text-xs">RANK {x.p}</div>
                <div className="font-display text-xl text-white mt-2">{x.t}</div>
                <div className={`font-display ${x.hl ? "text-4xl" : "text-3xl"} text-brand mt-3`} data-testid={`prize-amount-${i}`}>{formatINR(x.a)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certificates */}
      <CertificatesSection />

      {/* Sponsors */}
      <SponsorsSection />

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-5 sm:px-8 py-10 md:py-14 lg:py-16" data-testid="faq-section">
        <div className="label-eyebrow mb-3">FAQ</div>
        <h2 className="font-display text-4xl sm:text-5xl text-white leading-none">Questions, answered.</h2>
          <Accordion type="single" collapsible className="mt-8 space-y-2">
            {[
              { q: "Who can participate?", a: "Any currently-enrolled student with a valid institutional college email. Personal emails like gmail/yahoo/outlook are not accepted for registration." },
              { q: `What is the entry fee?`, a: `${formatINR(fee)} per person. This fee is strictly non-refundable under any circumstances. By proceeding to pay, you agree to our no-refund policy.` },
              { q: "How big can my team be?", a: `Teams must have between ${minSize} and ${maxSize} members. Teams below ${minSize} accepted members by the lock deadline will be auto-disqualified.` },
              { q: "Can I switch teams later?", a: "Yes, until the team lock deadline. After that, all team membership changes are frozen." },
              { q: "What if I can't ship in Round 1?", a: "Round 1 requires your idea write-up and a short recorded pitch video — not a working prototype. Focus on clearly explaining your problem and proposed solution." },
              { q: "Will there be support during the competition?", a: "Yes — the NoobSync Innovate team will be available throughout both rounds for queries and guidance via our official communication channels." },
              { q: "Do I get a certificate?", a: "Yes. Every participant who submits a valid Round 1 entry earns an officially recognised Certificate of Participation. Finalists and winners receive special Finalist and Certificate of Excellence awards. Each certificate carries a unique verification ID (NSI-2026-XXXX) that anyone — including recruiters — can verify on our site, and you can add it to your LinkedIn profile in one click." },
            ].map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-white/10 px-5" data-testid={`faq-${i}`}>
                <AccordionTrigger className="text-left font-display text-white hover:text-brand py-5 [&[data-state=open]>svg]:rotate-180">{f.q}</AccordionTrigger>
                <AccordionContent className="text-gray-400 pb-5 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
      </section>

      {/* CTA */}
      <section className="bg-[#080C18] border-t border-white/5 py-6 md:py-8 lg:py-8" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          {isClosed ? (
            <>
              <div className="label-eyebrow mb-4">What&apos;s next?</div>
              <h2 className="font-display text-4xl sm:text-6xl text-white leading-none">Don&apos;t miss the <span className="text-brand">next edition</span>.</h2>
              <p className="text-gray-400 mt-5 max-w-xl mx-auto">Drop your email and we&apos;ll ping you the moment the next IdeaThon goes live — no spam, just the launch announcement.</p>
              <div className="mt-8 flex justify-center">
                <WaitlistForm testid="closed-waitlist" />
              </div>
            </>
          ) : (
            <>
              <h2 className="font-display text-4xl sm:text-6xl text-white leading-none">Ready to build?</h2>
              <p className="text-gray-400 mt-5 max-w-xl mx-auto">Registration takes under 3 minutes. Verify your college email, pay {formatINR(fee)}, and start building your team.</p>
              <div className="mt-8">
                <Link to={ctaTarget} className="btn-primary inline-flex items-center gap-2" data-testid="cta-register">
                  {user ? "Continue Setup" : "Register Now"} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <p className="mt-4 text-sm text-gray-400" data-testid="cta-payment-trust">Secure payments powered by Razorpay</p>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}


// ============================================================
// COMING SOON (pre-launch) screen — replaces the entire landing
// when admin has not yet flipped status off "pre_launch".
// ============================================================
function ComingSoon({ config }) {
  const headline = config?.coming_soon_headline;
  const subtext = config?.coming_soon_subtext || config?.tagline;
  return (
    <div className="App" data-testid="coming-soon-page">
      <Navbar />
      <section className="relative overflow-hidden grid-bg">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 pt-20 sm:pt-28 pb-24 text-center">
          <div className="label-eyebrow mb-6">
            NoobSync Innovate · {config?.name || "IdeaThon"}
          </div>
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 border border-brand/40 bg-brand/10" data-testid="coming-soon-badge">
            <Sparkles className="w-3.5 h-3.5 text-brand" strokeWidth={2} />
            <span className="text-xs font-medium uppercase tracking-widest text-brand">Coming Soon</span>
          </div>
          <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.95] text-white" data-testid="coming-soon-title">
            {headline || (<>Something <span className="text-brand">big</span> is<br />about to launch.</>)}
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
            {subtext || "The next IdeaThon is being prepared. Drop your email and we'll notify you the moment registration opens."}
          </p>

          <div className="mt-12 flex justify-center">
            <WaitlistForm testid="prelaunch-waitlist" />
          </div>

          <div className="mt-16 grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto text-left">
            {[
              { i: Trophy, t: "Cash prizes", d: "A ₹30,000 cash prize pool, awarded to the top three teams." },
              { i: Code2,  t: "Real builds", d: "Two intense rounds. Ship something you'd actually demo." },
              { i: Users,  t: "Your crew",  d: `Form a team across colleges. Bring your best.` },
            ].map(({ i: Icon, t, d }, idx) => (
              <div key={idx} className="border-t border-white/10 pt-5" data-testid={`prelaunch-pillar-${idx}`}>
                <Icon className="w-5 h-5 text-brand mb-3" strokeWidth={1.5} />
                <div className="font-display text-lg text-white">{t}</div>
                <div className="text-sm text-gray-500 mt-1">{d}</div>
              </div>
            ))}
          </div>

          <div className="mt-12">
            <Link to="/editions" className="text-sm font-medium uppercase tracking-widest text-gray-400 hover:text-brand" data-testid="prelaunch-editions-link">
              View past editions →
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

// ============================================================
// Waitlist email-capture form — used on Coming Soon & Closed states.
// ============================================================
export function WaitlistForm({ testid = "waitlist-form" }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Please enter a valid email"); return; }
    setSubmitting(true);
    try {
      const eventId = newEventId();
      await api.post("/public/waitlist", { email, meta_event_id: eventId });
      setDone(true);
      track("waitlist_signup", { funnel_step: 0, event_id: eventId });
      toast.success("You're on the list. We'll be in touch.");
    } catch (err) {
      toast.error("Couldn't add you right now. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="card-surface border-l-2 border-brand max-w-md w-full flex items-center gap-3" data-testid={`${testid}-success`}>
        <CheckCircle className="w-5 h-5 text-brand flex-shrink-0" strokeWidth={2} />
        <div>
          <div className="font-display text-white text-lg">You&apos;re on the list.</div>
          <div className="text-xs text-gray-500 font-mono mt-1">{email}</div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3 w-full max-w-md" data-testid={testid}>
      <div className="relative flex-1">
        <Mail className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@college.edu"
          className="input-field w-full"
          style={{ paddingLeft: "2.5rem" }}
          data-testid={`${testid}-input`}
        />
      </div>
      <button type="submit" disabled={submitting} className="btn-primary flex items-center justify-center gap-2 whitespace-nowrap" data-testid={`${testid}-submit`}>
        {submitting ? <span className="spinner" /> : <><Sparkles className="w-4 h-4" /> Notify me</>}
      </button>
    </form>
  );
}
